import React, { useState, useEffect, useCallback } from 'react'
import graphql from 'babel-plugin-relay/macro'
import { fetchQuery } from 'config/amber-relay'
import Select from 'components/v2/Select'

export default function ProductSelect(props) {
  const { isService, isDisabled, value, onChange, familyId, onlyEnabled, ...selectProps } = props
  const [options, setOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  // Memoizar la función fetch para evitar recreaciones innecesarias
  const fetchProducts = useCallback(async (where) => {
    try {

      let finalWhere = where
      if (onlyEnabled) {
        finalWhere = {
          and: [
            { isDisabled: { eq: 'false' } },
            where
          ]
        }
      }

      setIsLoading(true)
      const { products } = await fetchQuery(
        graphql`
          query ProductSelectQuery($where: ProductWhere) {
            products(where: $where, orderBy: [code], order: ASC) {
              edges {
                node {
                  id
                  name
                }
              }
            }
          }
        `,
        { where: finalWhere }
      )

      const formattedOptions = products.edges.map(({ node }) => ({
        key: node.id,
        value: node.id,
        text: node.name,
      }))

      setOptions(formattedOptions)
      return formattedOptions
    } catch (error) {
      console.error('Error fetching products:', error)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Cargar opciones iniciales
  useEffect(() => {
    if (props.loadOnMount) {
      const conditions = []

      if (isService !== undefined) {
        if (isService) {
          conditions.push({ isService: { eq: 'true' } })
        } else {
          conditions.push({ isService: { eq: 'false' } })
        }
      }

      if (familyId) {
        conditions.push({ familyId: { eq: familyId } })
      }

      if (isDisabled !== undefined) {
        conditions.push({ isDisabled: { eq: isDisabled ? 'true' : 'false' } })
      }

      // Si hay condiciones, aplicarlas; si no, cargar todos los productos
      const where = conditions.length > 0 ? { and: conditions } : undefined
      fetchProducts(where)
    }
  }, [fetchProducts, isService, props.loadOnMount, familyId, isDisabled])

  // Función para buscar por valor (cuando el select necesita encontrar una opción específica)
  const getByValue = useCallback(async (value) => {
    if (!value) {
      return []
    }

    // Cuando se busca por ID específico, solo aplicar filtros si están definidos
    // No requerir familyId porque ya tenemos el ID del producto
    const conditions = [{ id: { eq: value } }]

    if (isService !== undefined) {
      if (isService) {
        conditions.push({ isService: { eq: 'true' } })
      } else {
        conditions.push({ isService: { eq: 'false' } })
      }
    }

    if (familyId) {
      conditions.push({ familyId: { eq: familyId } })
    }

    if (isDisabled !== undefined) {
      conditions.push({ isDisabled: { eq: isDisabled.toString() } })
    }

    return await fetchProducts({ and: conditions })
  }, [fetchProducts, isService, familyId, isDisabled])

  // Función para cargar opciones con búsqueda
  const loadOptions = useCallback(async (searchQuery) => {
    const conditions = []

    // Agregar búsqueda por nombre si hay query
    if (searchQuery) {
      conditions.push({ name: { iLike: `%${searchQuery}%` } })
    }

    // Aplicar filtros solo si están definidos
    if (isService !== undefined) {
      if (isService) {
        conditions.push({ isService: { eq: 'true' } })
      } else {
        conditions.push({ isService: { eq: 'false' } })
      }
    }

    if (familyId) {
      conditions.push({ familyId: { eq: familyId } })
    }

    if (isDisabled !== undefined) {
      conditions.push({ isDisabled: { eq: isDisabled.toString() === 'true' ? 'true' : 'false' } })
    }

    // Si hay condiciones, aplicarlas; si no, cargar todos los productos (o buscar por nombre si hay query)
    const where = conditions.length > 0 ? { and: conditions } : (searchQuery ? { name: { iLike: `%${searchQuery}%` } } : undefined)
    return await fetchProducts(where)
  }, [fetchProducts, isService, familyId, isDisabled])

  return (
    <Select
      {...selectProps}
      value={value}
      onChange={onChange}
      options={options}
      isLoading={isLoading}
      getByValue={getByValue}
      loadOptions={loadOptions}
    />
  )
}

ProductSelect.defaultProps = {
  label: 'Producto',
  placeholder: 'Producto',
  loadOnMount: true,
  onlyEnabled: true
}